import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { entityTypeLabels } from '@/lib/constants';
import { entityLabel, type EntityLabelSource } from '@/lib/diagramFields';
import type { Boundary, Entity, EntityType } from '@/types/diagram';

// Bounds mirror ThreatCat's `EntityCreate` contract (docs/threatcat-openapi.json).
export const ENTITY_NAME_MAX_LENGTH = 500;
export const ENTITY_TEXT_MAX_LENGTH = 50_000;
export const ROOT_BOUNDARY_VALUE = '__root__';

const ENTITY_TYPES = Object.keys(entityTypeLabels) as EntityType[];

export interface EntityFormDraft {
  name: string;
  type: EntityType;
  boundaryId: number | null;
  description: string;
  label: string;
}

export type EntityFormErrors = Partial<Record<keyof EntityFormDraft, string>>;

export function validateEntityDraft(draft: EntityFormDraft, boundaries: Boundary[]): EntityFormErrors {
  const errors: EntityFormErrors = {};
  if (!draft.name.trim()) {
    errors.name = 'Name is required.';
  } else if (draft.name.trim().length > ENTITY_NAME_MAX_LENGTH) {
    errors.name = `Name cannot exceed ${ENTITY_NAME_MAX_LENGTH} characters.`;
  }
  if (!ENTITY_TYPES.includes(draft.type)) {
    errors.type = 'Select a valid entity type.';
  }
  if (draft.boundaryId !== null && !boundaries.some((b) => b.id === draft.boundaryId)) {
    errors.boundaryId = 'Select a boundary of this diagram.';
  }
  if (draft.description.length > ENTITY_TEXT_MAX_LENGTH) {
    errors.description = `Description cannot exceed ${ENTITY_TEXT_MAX_LENGTH} characters.`;
  }
  if (draft.label.length > ENTITY_TEXT_MAX_LENGTH) {
    errors.label = `Technical information cannot exceed ${ENTITY_TEXT_MAX_LENGTH} characters.`;
  }
  return errors;
}

/** Apply the form to the entity, keeping every other field (id, position, style, …) untouched. */
export function applyEntityDraft(entity: Entity, draft: EntityFormDraft): Entity {
  const updated = {
    ...entity,
    name: draft.name.trim(),
    type: draft.type,
    // Root-level entities carry `boundary_id: null` in ThreatCat exports.
    boundary_id: draft.boundaryId as Entity['boundary_id'],
    description: draft.description,
    label: draft.label,
  };
  // The legacy field would otherwise shadow a cleared `label` in entityLabel().
  delete (updated as EntityLabelSource).technical_information;
  return updated;
}

function draftFromEntity(entity: Entity): EntityFormDraft {
  const boundaryId = entity.boundary_id ?? null;
  return {
    name: entity.name ?? '',
    type: ENTITY_TYPES.includes(entity.type) ? entity.type : 'default',
    boundaryId,
    description: entity.description ?? '',
    label: entityLabel(entity),
  };
}

interface EditEntityDialogProps {
  entity: Entity | null;
  boundaries: Boundary[];
  getBoundaryLabel: (boundary: Boundary) => string;
  isDraft: boolean;
  isSaving: boolean;
  saveError: string | null;
  saveSuccess: boolean;
  onCancel: () => void;
  onSave: (updated: Entity) => void;
}

export default function EditEntityDialog(props: EditEntityDialogProps) {
  const { entity, onCancel } = props;
  return (
    <Dialog open={entity !== null} onOpenChange={(open) => { if (!open) onCancel(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        {entity && <EditEntityForm key={entity.id} {...props} entity={entity} />}
      </DialogContent>
    </Dialog>
  );
}

function EditEntityForm({
  entity,
  boundaries,
  getBoundaryLabel,
  isDraft,
  isSaving,
  saveError,
  saveSuccess,
  onCancel,
  onSave,
}: EditEntityDialogProps & { entity: Entity }) {
  const [draft, setDraft] = useState<EntityFormDraft>(() => draftFromEntity(entity));
  const errors = validateEntityDraft(draft, boundaries);
  const hasErrors = Object.keys(errors).length > 0;
  const boundaryOptions = boundaries
    .map((b) => ({ id: b.id, label: getBoundaryLabel(b) }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const handleSave = () => {
    if (hasErrors || isSaving) return;
    onSave(applyEntityDraft(entity, draft));
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Edit Entity</DialogTitle>
        <DialogDescription>{`${entity.name} (ID: ${entity.id})`}</DialogDescription>
      </DialogHeader>

      <div className="space-y-3 py-2">
        <div className="space-y-1">
          <label htmlFor="entity-name" className="text-xs font-medium text-muted-foreground">Name</label>
          <Input
            id="entity-name"
            value={draft.name}
            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            placeholder="Entity name…"
            aria-invalid={Boolean(errors.name)}
          />
          {errors.name && <p data-field-error="name" className="text-xs text-destructive">{errors.name}</p>}
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">Type</label>
          <Select
            data-field="type"
            value={draft.type}
            onValueChange={(value) => setDraft((d) => ({ ...d, type: value as EntityType }))}
          >
            <SelectTrigger aria-label="Entity type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ENTITY_TYPES.map((type) => (
                <SelectItem key={type} value={type}>{entityTypeLabels[type]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.type && <p data-field-error="type" className="text-xs text-destructive">{errors.type}</p>}
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">Boundary</label>
          <Select
            data-field="boundary"
            value={draft.boundaryId === null ? ROOT_BOUNDARY_VALUE : String(draft.boundaryId)}
            onValueChange={(value) =>
              setDraft((d) => ({ ...d, boundaryId: value === ROOT_BOUNDARY_VALUE ? null : Number(value) }))
            }
          >
            <SelectTrigger aria-label="Entity boundary">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ROOT_BOUNDARY_VALUE}>No boundary / Root</SelectItem>
              {boundaryOptions.map((b) => (
                <SelectItem key={b.id} value={String(b.id)}>{b.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.boundaryId && <p data-field-error="boundaryId" className="text-xs text-destructive">{errors.boundaryId}</p>}
        </div>
        <div className="space-y-1">
          <label htmlFor="entity-description" className="text-xs font-medium text-muted-foreground">Description</label>
          <Textarea
            id="entity-description"
            value={draft.description}
            onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
            placeholder="Description…"
            rows={3}
          />
          {errors.description && <p data-field-error="description" className="text-xs text-destructive">{errors.description}</p>}
        </div>
        <div className="space-y-1">
          <label htmlFor="entity-label" className="text-xs font-medium text-muted-foreground">Technical information</label>
          <Textarea
            id="entity-label"
            value={draft.label}
            onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))}
            placeholder="Technologies, versions, hosting…"
            rows={4}
          />
          {errors.label && <p data-field-error="label" className="text-xs text-destructive">{errors.label}</p>}
        </div>

        {saveError && (
          <div className="flex items-center gap-2 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            {saveError}
          </div>
        )}
        {saveSuccess && (
          <div className="flex items-center gap-2 rounded-md bg-green-50 px-3 py-2 text-sm text-green-700 dark:bg-green-900/20 dark:text-green-400">
            <CheckCircle2 className="h-4 w-4 shrink-0" />
            Saved successfully
          </div>
        )}
      </div>

      <DialogFooter className="gap-2">
        <Button variant="outline" data-entity-cancel onClick={onCancel} disabled={isSaving}>
          Cancel
        </Button>
        <Button data-entity-save onClick={handleSave} disabled={isSaving || hasErrors}>
          {isSaving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving…</> : isDraft ? 'Save Draft' : 'Save & Sync'}
        </Button>
      </DialogFooter>
    </>
  );
}
